import {
  DetailedDrop_LiveShow,
  DetailedDrop_LiveShow_LiveShowState as LiveShowState,
} from '@aboutyou/legacy-shared/src/grpc_client/grips/protos/shared/detailed_drop'
import {
  formatDateWithWrittenMonth,
  getFormattedDate,
  getTimeFromDate,
  isToday,
  isTomorrow,
} from '@aboutyou/legacy-shared/src/localization/date'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { cdnImageUrl } from 'helpers/image'
import React from 'react'
import { useLocaleStore } from 'store/context'
import styled from 'styled-components'
import { Icon, IconSize } from 'uiComponents/atoms/Icon/Icon'
import { IconName } from 'uiComponents/atoms/Icon/IconName'
import { fsMS, fsXXS, fwBold } from 'uiComponents/constants/typography'
import { LiveShowStartButton } from './LiveShowStartButton'

export const LiveShow: React.FC<{
  liveShow: DetailedDrop_LiveShow
  startLiveShow: (showId: string) => void
}> = ({ liveShow, startLiveShow }) => {
  const { t } = useTranslation()
  const locale = useLocaleStore(store => store.locale)

  if (!liveShow) {
    return null
  }

  const liveShowImage = cdnImageUrl(liveShow.squareImageHash, {
    trim: 1,
    height: 100,
    width: 100,
  })

  const isLiveShowStartButtonVisible = [
    LiveShowState.LIVE_SHOW_STATE_LIVE,
    LiveShowState.LIVE_SHOW_STATE_ENDED,
  ].includes(liveShow.state)

  const liveShowDate = () => {
    if (!liveShow.startsAt) {
      return
    }

    const startsAt = new Date(liveShow.startsAt)

    switch (liveShow.state) {
      case LiveShowState.LIVE_SHOW_STATE_NOT_STARTED:
        const startTime = getTimeFromDate(startsAt)

        if (isToday(startsAt)) {
          return `${t('Today')} ${startTime}`
        }

        if (isTomorrow(startsAt)) {
          return `${t('Tomorrow')} ${startTime}`
        }

        return `${formatDateWithWrittenMonth(startsAt, locale)} ${startTime}`

      case LiveShowState.LIVE_SHOW_STATE_LIVE:
        const elapsedMinutes = (() => {
          const now = new Date()
          return Math.abs(
            Math.round((now.getTime() - startsAt.getTime()) / 60000),
          )
        })()

        return t('Seit {{elapsedMinutes}} minuten live', { elapsedMinutes })

      case LiveShowState.LIVE_SHOW_STATE_ENDED:
        return `${t('Vom')} ${getFormattedDate(startsAt, 'dd.mm.yyyy')}`
      default:
        return ''
    }
  }

  return (
    <Wrapper data-testid="LiveShow-Wrapper">
      <img data-testid="LiveShow-img" src={liveShowImage}></img>

      <LiveShowDetails data-testid="LiveShow-Details">
        <LiveShowTime>
          <StyledTimeIcon asset={IconName.IcCalendar} size={IconSize.S} />
          {liveShowDate()}
        </LiveShowTime>
        <LiveShowTitle>{liveShow.title}</LiveShowTitle>
      </LiveShowDetails>
      {isLiveShowStartButtonVisible && (
        <LiveShowStartButton
          liveShow={liveShow}
          startLiveShow={startLiveShow}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 32px 0;
`
const StyledTimeIcon = styled(Icon)`
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  margin-right: 8px;
`

const LiveShowDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 24px;
`

const LiveShowTime = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fsXXS}px;
  ${fwBold}
  text-transform: uppercase;
`

const LiveShowTitle = styled.p`
  font-size: ${fsMS}px;
  ${fwBold}
  margin-top: 10px;
`
